import { $ } from "js/vendor";
import { app } from "js/namespaces";
import React, { Component, Fragment } from "reactn";
import ReactDOM from "react-dom";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import { dialogTheme } from "./materialThemeOverrides";
import addReactNDevTools from "reactn-devtools";
import { UNSAFE_FirebaseAuthProvider } from "js/react/views/Auth/FirebaseAuthContext";
import { UNSAFE_FirebaseUserProvider } from "js/react/views/Auth/FirebaseUserContext";

const _rerouteUnmountables = [];
const pushMount = element => {
    let index = _rerouteUnmountables.findIndex(x => x === element);
    if (index < 0) {
        _rerouteUnmountables.push(element);
    }
};
const popMount = element => {
    let index = _rerouteUnmountables.findIndex(x => x === element);
    if (index > -1) {
        _rerouteUnmountables.splice(index, 1);
    }
};

addReactNDevTools();

export const reactUnmountAllForReroute = () => {
    while (_rerouteUnmountables.length) {
        let element = _rerouteUnmountables.pop();
        ReactDOM.unmountComponentAtNode(element);
    }
};

export const reactUnmount = element => {
    ReactDOM.unmountComponentAtNode(element);
    popMount(element);
};

export const reactMount = (dom, element, unmountOnReroute = false) => {
    if (unmountOnReroute) {
        pushMount(element);
    }
    ReactDOM.render(
        dom,
        element
    );
};

// React's Context is not available in ad-hoc ReactDOM.render roots, so we grab global auth,
// assuming that it was safely initalized elsewhere already
function provideAuth(children) {
    return (
        <UNSAFE_FirebaseAuthProvider>
            <UNSAFE_FirebaseUserProvider>
                {children}
            </UNSAFE_FirebaseUserProvider>
        </UNSAFE_FirebaseAuthProvider>
    );
}

export default function renderReactRoot(Component, props = {}, element, unmountOnReroute = false) {
    reactMount(
        provideAuth(
            <MuiThemeProvider theme={dialogTheme}>
                <Component {...props} />
            </MuiThemeProvider>
        ),
        element,
        unmountOnReroute
    );

    return () => {
        reactUnmount(element);
    };
}

export function renderReactRootJSX(jsx, element, unmountOnReroute = false) {
    reactMount(
        provideAuth(
            <MuiThemeProvider theme={dialogTheme}>
                {jsx}
            </MuiThemeProvider>
        ),
        element,
        unmountOnReroute
    );

    return () => {
        reactUnmount(element);
    };
}

// Dialogs by default do want to have their dom unmounted on reroute
export function renderReactDialog(Component, props = {}, zIndex = 1300) {
    let unmountOnReroute = true;
    if (props.unmountOnReroute !== undefined) {
        unmountOnReroute = props.unmountOnReroute;
        delete props.unmountOnReroute;
    }
    let registerDialog = true;
    if (props.registerDialog !== undefined) {
        registerDialog = props.registerDialog;
        delete props.registerDialog;
    }
    let $dialogContainer = $("body").addEl($.div("dialog-container"));
    $dialogContainer.attr("dialog-container");
    $dialogContainer.zIndex(zIndex);

    let isShowingVerificationBanner = false;
    if (app && app.user) {
        isShowingVerificationBanner = app.mainView && app.mainView.shouldRenderVerificationBanner(app.user.getAuthUser());
    }
    if (isShowingVerificationBanner) {
        $dialogContainer.addClass("verify-email");
    }
    if (props.overlayIsUnblocking) {
        $dialogContainer.addClass("unblocking");
        delete props.overlayIsUnblocking;
    }
    let unmount = result => {
        if (
            registerDialog &&
            dialog &&
            app?.dialogManager
        ) {
            app.dialogManager.unregisterDialog(dialog);
        }
        reactUnmount($dialogContainer[0]);
        $dialogContainer.remove();
        const { onClose } = props;
        onClose && onClose(result);
    };
    props.closeDialog = unmount;
    let ref = React.createRef();
    reactMount(
        provideAuth(
            <MuiThemeProvider theme={dialogTheme}>
                <Component ref={ref} {...props} closeDialog={unmount} />
            </MuiThemeProvider>
        ),
        $dialogContainer[0],
        unmountOnReroute
    );
    const dialog = ref.current;
    // Using 'close' instead of 'closeDialog' because it
    //   clashes with various MaterialUI components
    if (!dialog.close) {
        dialog.close = unmount;
    }
    if (
        registerDialog &&
        app?.dialogManager
    ) {
        app.dialogManager.registerDialog(dialog);
    }
    return dialog;
}

export class DialogJSX extends Component {
    render() {
        return (
            <Fragment>{this.props.children}</Fragment>
        );
    }
}

// Dialogs by default do want to have their dom unmounted on reroute
export function renderReactDialogFromJSX(jsx, options) {
    options = {
        overlayIsUnblocking: false,
        unmountOnReroute: true,
        registerDialog: true,
        ...options,
    };
    let $dialogContainer = $("body").addEl($.div("dialog-container"));
    $dialogContainer.attr("dialog-container");

    let unmount = () => {
        if (
            options.registerDialog &&
            dialog &&
            app?.dialogManager
        ) {
            app.dialogManager.unregisterDialog(dialog);
        }
        reactUnmount($dialogContainer[0]);
        $dialogContainer.remove();
    };
    if (options.overlayIsUnblocking) {
        $dialogContainer.addClass("unblocking");
    }

    let ref = React.createRef();
    reactMount(
        provideAuth(
            <MuiThemeProvider theme={dialogTheme}>
                <DialogJSX ref={ref}>
                    {jsx}
                </DialogJSX>
            </MuiThemeProvider>
        ),
        $dialogContainer[0],
        options.unmountOnReroute
    );
    const dialog = ref.current;
    // Using 'close' instead of 'closeDialog' because it
    //   clashes with various MaterialUI components
    if (!dialog.close) {
        dialog.close = unmount;
    }
    if (
        options.registerDialog &&
        app?.dialogManager
    ) {
        app.dialogManager.registerDialog(dialog);
    }
    return dialog;
}
